import { Button } from "@/components/ui/UIElements/Button";
import React, { useState } from "react";
import { apiClient } from "@finitless/shared/lib";
import { useAuth } from "@finitless/shared/contexts";
import { Icon } from "../../";
import { IntegrationLogs, PluginModal, SyncButtons } from "../";
import { usePluginEnvironment } from "../../../../hooks";

const IntegrationActions = ({
  integration,
  business,
  parseTemplate,
  onSetupClick,
  onDisconnect,
  isDisconnecting,
  onConfigComplete,
}) => {
  const [showLogs, setShowLogs] = useState(false);
  const [pluginModal, setPluginModal] = useState({
    open: false,
    title: "",
    url: "",
  });
  const { currentProject } = useAuth();
  const token = apiClient.getAuthToken();
  const {
    isSandbox,
    isLoading: isEnvironmentLoading,
    toggleEnvironment,
    isToggling,
  } = usePluginEnvironment(integration?.plugin?.root);

  const handleOpenPluginDetails = (e, plugin, linkKey) => {
    e.stopPropagation();

    if (!plugin?.links?.[linkKey] || !parseTemplate) return;

    // Parse template variables with actual project data
    const variables = {
      project_code: currentProject || "",
      access_token: token || "",
      business_id: business?.id,
    };

    // If clicking on general_settings, open setup_url instead
    let targetUrl = plugin?.links[linkKey];
    if (linkKey === "general_settings" && plugin?.setup_url) {
      targetUrl = plugin.setup_url;
    }

    const url = parseTemplate(plugin?.root + targetUrl, variables);

    setPluginModal({
      open: true,
      title: plugin.name,
      url: url,
    });
  };

  const handleOpenLogs = () => {
    setShowLogs(true);
  };

  const handleCloseLogs = () => {
    setShowLogs(false);
  };

  const handleClosePluginModal = () => {
    setPluginModal({ open: false, title: "", url: "" });
    onConfigComplete && onConfigComplete();
  };

  const capitalize = (str) => {
    return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!integration?.plugin?.links) return null;

  return (
    <>
      {/* Settings Section */}
      <div className="space-y-3">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-xs">Settings</p>
          <div className="flex flex-wrap justify-center gap-2">
            {/* POS Setup button */}
            {onSetupClick && (
              <Button
                variant="ghost"
                onClick={onSetupClick}
                className="border-primary/30 bg-primary/20 hover:border-primary/50 hover:bg-primary/30 inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium text-[var(--od-action-content)] transition-colors"
              >
                <Icon name="Settings" className="mr-2 h-4 w-4" />
                Setup
              </Button>
            )}

            {/* Dynamic buttons from plugin.links */}
            {Object.keys(integration.plugin.links).map((key) => (
              <Button
                variant="ghost"
                key={key}
                onClick={(e) =>
                  handleOpenPluginDetails(e, integration.plugin, key)
                }
                className="border-primary/30 bg-primary/20 hover:border-primary/50 hover:bg-primary/30 inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium text-[var(--od-action-content)] transition-colors"
              >
                <Icon name="ExternalLink" className="mr-2 h-4 w-4" />
                {key === "general_settings" ? "Settings" : capitalize(key)}
              </Button>
            ))}

            {/* Sandbox Mode Toggle */}
            {integration?.plugin?.root && (
              <Button
                variant="ghost"
                onClick={toggleEnvironment}
                disabled={isToggling || isEnvironmentLoading}
                className={`inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isSandbox
                    ? "border-warning/30 bg-warning/20 text-warning hover:border-warning/50 hover:bg-warning/30"
                    : "border-primary/30 bg-primary/20 hover:border-primary/50 hover:bg-primary/30 text-[var(--od-action-content)]"
                }`}
              >
                {isToggling ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                ) : (
                  <Icon
                    name={isSandbox ? "ToggleRight" : "ToggleLeft"}
                    className="mr-2 h-4 w-4"
                  />
                )}
                {isEnvironmentLoading
                  ? "Loading..."
                  : isSandbox
                    ? "Sandbox Mode (ON)"
                    : "Sandbox Mode (OFF)"}
              </Button>
            )}
          </div>
        </div>

        {/* Action Section */}
        <div className="border-border border-t pt-3 text-center">
          <p className="text-muted-foreground mb-3 text-xs">Action</p>
          <SyncButtons
            businessId={business?.id}
            integrationId={integration?.id}
          />
        </div>

        {/* Additional Options Section */}
        <div className="border-border border-t pt-3 text-center">
          <p className="text-muted-foreground mb-3 text-xs">
            Additional Options
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="ghost"
              onClick={handleOpenLogs}
              className="border-primary/30 bg-primary/20 hover:border-primary/50 hover:bg-primary/30 inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium text-[var(--od-action-content)] transition-colors"
            >
              <Icon name="FileText" className="mr-2 h-4 w-4" />
              View Logs
            </Button>

            {/* Disconnect button */}
            {onDisconnect && (
              <Button
                variant="ghost"
                onClick={onDisconnect}
                disabled={isDisconnecting}
                className="border-destructive/30 bg-destructive/20 text-destructive hover:border-destructive/50 hover:bg-destructive/30 inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDisconnecting ? (
                  <div className="border-destructive mr-2 h-4 w-4 animate-spin rounded-full border-b-2"></div>
                ) : (
                  <Icon name="X" className="mr-2 h-4 w-4" />
                )}
                Disconnect POS
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Integration Logs Modal */}
      <IntegrationLogs
        isOpen={showLogs}
        onClose={handleCloseLogs}
        model="Business"
        modelId={business?.id}
        pluginId={integration?.plugin_id}
      />

      {/* Plugin Modal */}
      <PluginModal
        isOpen={pluginModal.open}
        onClose={handleClosePluginModal}
        title={pluginModal.title}
        url={pluginModal.url}
      />
    </>
  );
};

export default IntegrationActions;
